<template>
  <div class="utm-config">
    <!-- 1. Master toggle: Enable UTM tracking -->
    <div class="flex align-items-center gap-2 mb-2">
      <InputSwitch
        v-model="localData.utm_enabled"
        input-id="utm_master"
        :disabled="globalUtmDisabled"
      />
      <label for="utm_master" class="font-semibold cursor-pointer">
        {{ t('automatedScenarios.actions.sending_channel.utm_config.masterToggle') }}
      </label>
    </div>
    <div
      v-if="globalUtmDisabled"
      class="utm-global-disabled-notice"
    >
      {{ t('automatedScenarios.actions.sending_channel.utm_config.globallyDisabled') }}
    </div>
    <small v-else-if="localData.utm_enabled" class="block mb-3 ml-4 text-color-secondary">
      {{ t('automatedScenarios.actions.sending_channel.utm_config.masterHint') }}
    </small>

    <template v-if="localData.utm_enabled">
      <!-- 2. Sub-toggle: Use custom params -->
      <div class="flex align-items-center gap-2 mb-2 ml-4">
        <InputSwitch
          v-model="localData.utm_use_custom"
          input-id="utm_use_custom"
        />
        <label for="utm_use_custom" class="cursor-pointer">
          {{ t('automatedScenarios.actions.sending_channel.utm_config.useCustom') }}
        </label>
      </div>
      <small v-if="localData.utm_use_custom" class="block mb-3 ml-5 text-color-secondary">
        {{ t('automatedScenarios.actions.sending_channel.utm_config.useCustomHint') }}
      </small>

      <!-- Info: global defaults will be used -->
      <div v-if="!localData.utm_use_custom" class="ml-5 mb-2">
        <small class="text-color-secondary">
          {{ t('automatedScenarios.actions.sending_channel.utm_config.usingGlobalDefaults') }}
        </small>
      </div>

      <!-- 3. Per-param rows -->
      <div v-if="localData.utm_use_custom" class="ml-4 mt-3">
        <!-- Header row -->
        <div class="flex align-items-center gap-2 mb-2">
          <div style="width: 2rem" />
          <div class="w-12rem text-color-secondary text-sm font-semibold">
            {{ t('automatedScenarios.actions.sending_channel.utm_config.paramHeader') }}
          </div>
          <div class="flex-grow-1 text-color-secondary text-sm font-semibold">
            {{ t('automatedScenarios.actions.sending_channel.utm_config.valueHeader') }}
          </div>
        </div>

        <!-- Standard param rows -->
        <div
          v-for="param in utmParams"
          :key="param.key"
          class="flex align-items-center gap-2 mb-2"
        >
          <InputSwitch
            v-model="localData[param.enabledKey]"
            :input-id="'utm_param_' + param.key"
            class="utm-param-switch"
          />
          <label
            :for="'utm_param_' + param.key"
            class="w-12rem flex-shrink-0 text-sm cursor-pointer"
            :class="{ 'text-color-secondary': !localData[param.enabledKey] }"
          >
            {{ t(param.labelKey) }}
          </label>
          <div class="p-inputgroup flex-grow-1">
            <InputText
              v-model="localData[param.valueKey]"
              :disabled="!localData[param.enabledKey]"
              :placeholder="param.placeholder"
              class="w-full"
            />
            <span
              class="p-inputgroup-addon cursor-pointer utm-var-btn"
              :class="{ 'utm-var-btn-disabled': !localData[param.enabledKey] }"
              :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
              @click="localData[param.enabledKey] && openVariablePicker($event, param.valueKey)"
            >
              <i class="fa-solid fa-code" />
            </span>
          </div>
        </div>

        <!-- Custom UTM Parameters -->
        <div class="mt-3">
          <div class="text-sm font-semibold text-color-secondary mb-2">
            {{ t('automatedScenarios.actions.sending_channel.utm_config.customParams.title') }}
          </div>
          <div
            v-for="(param, index) in localData.utm_custom_params"
            :key="index"
            class="flex align-items-center gap-2 mb-2"
          >
            <div style="width: 2rem" />
            <InputText
              v-model="param.key"
              :placeholder="t('automatedScenarios.actions.sending_channel.utm_config.customParams.keyPlaceholder')"
              class="w-12rem flex-shrink-0"
            />
            <div class="p-inputgroup flex-grow-1">
              <InputText
                v-model="param.value"
                :placeholder="t('automatedScenarios.actions.sending_channel.utm_config.customParams.valuePlaceholder')"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePickerCustom($event, index)"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <Button
              icon="fa-solid fa-trash-can"
              class="p-button-text p-button-danger p-button-sm"
              @click="removeCustomParam(index)"
            />
          </div>
          <Button
            :label="t('automatedScenarios.actions.sending_channel.utm_config.customParams.addButton')"
            icon="fa-solid fa-plus"
            class="p-button-outlined p-button-sm mt-1"
            @click="addCustomParam"
          />
        </div>
      </div>

      <!-- Variable Picker Overlay (shared by all fields) -->
      <OverlayPanel
        ref="variablePickerRef"
        append-to="body"
      >
        <div class="flex flex-column" style="width: 320px;">
          <InputText
            v-model="variableFilter"
            :placeholder="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.searchPlaceholder')"
            class="w-full mb-2 p-inputtext-sm"
          />
          <div class="utm-var-list">
            <div
              v-for="opt in filteredOptions"
              :key="opt.value"
              class="utm-var-option"
              @click="selectVariable(opt)"
            >
              <div class="flex align-items-center gap-2">
                <i
                  v-if="opt.isCustom"
                  class="fa-solid fa-puzzle-piece custom-icon"
                />
                <div class="flex flex-column">
                  <span class="text-sm">{{ opt.displayLabel }}</span>
                  <span class="text-xs" style="color: var(--text-color-secondary);">{{ opt.value }}</span>
                </div>
              </div>
              <span
                v-if="opt.isCustom"
                class="custom-badge-inline"
              >
                CUSTOM
              </span>
            </div>
            <div
              v-if="filteredOptions.length === 0"
              class="px-3 py-2 text-sm text-color-secondary"
            >
              {{ t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.noResults') }}
            </div>
          </div>
        </div>
      </OverlayPanel>
    </template>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  reactive,
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';
import { UserState } from '@/composables/User';
import { fetchShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import { useUtmDropdownOptions, DropdownOption } from '@/composables/flows/useUtmDropdownOptions';

export interface UtmCustomParam {
  key: string;
  value: string;
}

export interface UtmData {
  utm_enabled: boolean;
  utm_use_custom: boolean;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_id: string;
  utm_content: string;
  utm_term: string;
  utm_source_enabled: boolean;
  utm_medium_enabled: boolean;
  utm_campaign_enabled: boolean;
  utm_id_enabled: boolean;
  utm_content_enabled: boolean;
  utm_term_enabled: boolean;
  utm_custom_params: UtmCustomParam[];
}

const BASE_I18N = 'automatedScenarios.actions.sending_channel.utm_config';

export default defineComponent({
  name: 'UtmConfig',

  components: {
    InputSwitch,
    InputText,
    OverlayPanel,
    Button,
  },

  props: {
    modelValue: {
      type: Object as PropType<UtmData>,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const { dropdownOptions, loadCustomData } = useUtmDropdownOptions(t);

    const localData = reactive<UtmData>({
      utm_enabled: props.modelValue.utm_enabled ?? true,
      utm_use_custom: props.modelValue.utm_use_custom ?? false,
      utm_source: props.modelValue.utm_source ?? '',
      utm_medium: props.modelValue.utm_medium ?? '',
      utm_campaign: props.modelValue.utm_campaign ?? '',
      utm_id: props.modelValue.utm_id ?? '',
      utm_content: props.modelValue.utm_content ?? '',
      utm_term: props.modelValue.utm_term ?? '',
      utm_source_enabled: props.modelValue.utm_source_enabled ?? true,
      utm_medium_enabled: props.modelValue.utm_medium_enabled ?? true,
      utm_campaign_enabled: props.modelValue.utm_campaign_enabled ?? true,
      utm_id_enabled: props.modelValue.utm_id_enabled ?? false,
      utm_content_enabled: props.modelValue.utm_content_enabled ?? false,
      utm_term_enabled: props.modelValue.utm_term_enabled ?? false,
      utm_custom_params: props.modelValue.utm_custom_params ?? [],
    });

    // Param definitions
    const utmParams = [
      { key: 'source', valueKey: 'utm_source', enabledKey: 'utm_source_enabled', labelKey: `${BASE_I18N}.labels.source`, placeholder: 'shopimind' },
      { key: 'medium', valueKey: 'utm_medium', enabledKey: 'utm_medium_enabled', labelKey: `${BASE_I18N}.labels.medium`, placeholder: '{var=channel}' },
      { key: 'campaign', valueKey: 'utm_campaign', enabledKey: 'utm_campaign_enabled', labelKey: `${BASE_I18N}.labels.campaign`, placeholder: '{var=campaign_name}' },
      { key: 'id', valueKey: 'utm_id', enabledKey: 'utm_id_enabled', labelKey: `${BASE_I18N}.labels.id`, placeholder: '' },
      { key: 'content', valueKey: 'utm_content', enabledKey: 'utm_content_enabled', labelKey: `${BASE_I18N}.labels.content`, placeholder: '' },
      { key: 'term', valueKey: 'utm_term', enabledKey: 'utm_term_enabled', labelKey: `${BASE_I18N}.labels.term`, placeholder: '' },
    ];

    // Custom params management
    const addCustomParam = () => {
      localData.utm_custom_params.push({ key: '', value: '' });
    };

    const removeCustomParam = (index: number) => {
      localData.utm_custom_params.splice(index, 1);
    };

    // Variable picker state
    const variablePickerRef = ref();
    const variableFilter = ref('');
    const activeField = ref('');
    const activeCustomIndex = ref(-1);

    const filteredOptions = computed(() => {
      if (!variableFilter.value) return dropdownOptions.value;
      const q = variableFilter.value.toLowerCase();
      return dropdownOptions.value.filter(
        (opt) => opt.displayLabel.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
      );
    });

    const openVariablePicker = (event: Event, fieldKey: string) => {
      activeField.value = fieldKey;
      activeCustomIndex.value = -1;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const openVariablePickerCustom = (event: Event, index: number) => {
      activeField.value = '';
      activeCustomIndex.value = index;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const selectVariable = (opt: DropdownOption) => {
      if (activeCustomIndex.value >= 0) {
        localData.utm_custom_params[activeCustomIndex.value].value = opt.value;
      } else {
        (localData as any)[activeField.value] = opt.value;
      }
      variablePickerRef.value.hide();
    };

    // Guard to prevent circular watcher updates
    let isEmitting = false;

    // Emit on changes
    watch(localData, () => {
      isEmitting = true;
      emit('update:modelValue', { ...localData });
      nextTick(() => { isEmitting = false; });
    }, { deep: true });

    // Sync from parent (only if not our own emission echoing back)
    watch(() => props.modelValue, (newVal) => {
      if (isEmitting) return;
      const keys = Object.keys(localData) as (keyof UtmData)[];
      keys.forEach((key) => {
        if (newVal[key] !== undefined && (localData as any)[key] !== newVal[key]) {
          (localData as any)[key] = newVal[key];
        }
      });
    }, { deep: true });

    // Track whether UTM is globally disabled at shop level
    const globalUtmDisabled = ref(false);

    onMounted(async () => {
      const shopId = UserState.activeShop?.id ?? 0;

      // Load global UTM active config
      try {
        const config = await fetchShopConfigurationsNest(shopId, ['shopRequiredDataUtm_activeConfig']);
        if (config) {
          const activeConfig = config.shopRequiredDataUtm_activeConfig;
          const isGloballyEnabled = activeConfig !== '0';
          globalUtmDisabled.value = !isGloballyEnabled;

          if (!isGloballyEnabled) {
            // UTM disabled globally → force off
            localData.utm_enabled = false;
          } else if (props.modelValue.utm_enabled === undefined || props.modelValue.utm_enabled === null) {
            // New operator, no explicit setting → default from global config
            localData.utm_enabled = true;
          }
        }
      } catch {
        // Default: not disabled
      }

      // Load custom data properties for dropdown
      await loadCustomData();
    });

    return {
      t,
      localData,
      globalUtmDisabled,
      utmParams,
      dropdownOptions,
      addCustomParam,
      removeCustomParam,
      variablePickerRef,
      variableFilter,
      filteredOptions,
      openVariablePicker,
      openVariablePickerCustom,
      selectVariable,
    };
  },
});
</script>

<style scoped>
.utm-global-disabled-notice {
  background-color: #fff8e1;
  border-left: 3px solid #c5a83b;
  color: #7a6a2a;
  padding: 0.625rem 0.75rem;
  margin: 0.5rem 0 0.75rem 0;
  font-size: 0.8rem;
  line-height: 1.4;
  border-radius: 0 4px 4px 0;
}

.utm-param-switch {
  transform: scale(0.7);
  flex-shrink: 0;
}

.utm-var-btn {
  transition: background-color 0.2s;
}

.utm-var-btn:hover:not(.utm-var-btn-disabled) {
  background: var(--primary-color);
  color: var(--primary-color-text);
}

.utm-var-btn-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.utm-var-list {
  max-height: 280px;
  overflow-y: auto;
}

.utm-var-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: var(--border-radius);
  transition: background-color 0.15s;
}

.utm-var-option:hover {
  background: var(--surface-hover);
}

.custom-icon {
  color: #8b5cf6;
  font-size: 0.85rem;
}

.custom-badge-inline {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  text-transform: uppercase;
  white-space: nowrap;
}
</style>
